import { useState } from 'react';
import { motion } from 'framer-motion';

export default function ExecutiveInquirySection() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');

    const form = e.currentTarget;
    const formData = new FormData(form);
    const data = new URLSearchParams(formData as any);

    try {
      const response = await fetch('https://readdy.ai/api/form/d69dkqlbiueuoqm1rsug', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: data.toString(),
      });

      if (!response.ok) throw new Error('Submit failed');
      setFormStatus('success');
      form.reset();
    } catch {
      setFormStatus('error');
    }
  };

  return (
    <section id="inquiry" className="relative bg-[#0A0E27] py-32">
      <div className="max-w-[1400px] mx-auto px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-amber-400 text-xs tracking-[0.3em] uppercase">
              Partnership Inquiry
            </span>
            <h2 className="text-4xl md:text-5xl font-black text-white mt-4 leading-tight">
              Let's Build Your
              <br />
              Growth Strategy
            </h2>
            <p className="text-white/60 text-lg mt-6 leading-relaxed">
              Our partnership team will prepare a customized proposal within 48 hours,
              <br />
              tailored to your market objectives and investment criteria.
            </p>

            <div className="mt-10 space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-amber-400/10 flex items-center justify-center">
                  <i className="ri-phone-line text-amber-400 text-xl"></i>
                </div>
                <div>
                  <p className="text-white font-semibold">Direct Partnership Line</p>
                  <p className="text-white/50 text-sm">Dedicated account representative</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-amber-400/10 flex items-center justify-center">
                  <i className="ri-user-star-line text-amber-400 text-xl"></i>
                </div>
                <div>
                  <p className="text-white font-semibold">Expert Consultation</p>
                  <p className="text-white/50 text-sm">10+ years OOH industry experience</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-amber-400/10 flex items-center justify-center">
                  <i className="ri-shield-check-line text-amber-400 text-xl"></i>
                </div>
                <div>
                  <p className="text-white font-semibold">Confidential Review</p>
                  <p className="text-white/50 text-sm">NDA available upon request</p>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <form
              id="executive-inquiry-form-en"
              data-readdy-form
              onSubmit={handleSubmit}
              className="bg-white rounded-3xl p-10 shadow-2xl"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">Full Name *</label>
                  <input
                    type="text"
                    name="name"
                    required
                    className="w-full px-4 py-3.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-amber-400 transition-colors"
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">Title *</label>
                  <input
                    type="text"
                    name="position"
                    required
                    className="w-full px-4 py-3.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-amber-400 transition-colors"
                    placeholder="CEO / CMO / VP Marketing"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">Company *</label>
                  <input
                    type="text"
                    name="company"
                    required
                    className="w-full px-4 py-3.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-amber-400 transition-colors"
                    placeholder="Company Inc."
                  />
                </div>
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">Email *</label>
                  <input
                    type="email"
                    name="email"
                    required
                    className="w-full px-4 py-3.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-amber-400 transition-colors"
                    placeholder="john@company.com"
                  />
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-gray-700 text-sm font-medium mb-2">Annual Marketing Budget *</label>
                <select
                  name="budget"
                  required
                  className="w-full px-4 py-3.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-amber-400 transition-colors cursor-pointer bg-white"
                >
                  <option value="">Select budget range</option>
                  <option value="under-500k">Under $500K</option>
                  <option value="500k-2m">$500K - $2M</option>
                  <option value="2m-5m">$2M - $5M</option>
                  <option value="over-5m">Over $5M</option>
                </select>
              </div>

              <div className="mb-6">
                <label className="block text-gray-700 text-sm font-medium mb-3">Asset Types of Interest</label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {['Digital Billboard', 'Highway', 'Airport', 'Downtown', 'Convention', 'Stadium'].map((type) => (
                    <label key={type} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        name="asset_types"
                        value={type}
                        className="w-4 h-4 rounded border-gray-300 text-amber-400 focus:ring-amber-400 cursor-pointer"
                      />
                      <span className="text-gray-600 text-sm">{type}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="mb-8">
                <label className="block text-gray-700 text-sm font-medium mb-2">Investment Goals & Objectives</label>
                <textarea
                  name="message"
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-amber-400 transition-colors resize-none"
                  placeholder="Tell us about your market expansion goals and investment criteria"
                ></textarea>
              </div>

              <button
                type="submit"
                disabled={formStatus === 'submitting'}
                className="w-full py-4 bg-amber-400 text-black text-base font-semibold rounded-lg hover:bg-amber-300 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {formStatus === 'submitting' ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>
                    Submitting...
                  </span>
                ) : (
                  'Request Partnership Consultation'
                )}
              </button>

              {formStatus === 'success' && (
                <p className="text-green-600 text-sm text-center mt-4">
                  Thank you for your inquiry. Our partnership team will contact you within 48 hours.
                </p>
              )}
              {formStatus === 'error' && (
                <p className="text-red-600 text-sm text-center mt-4">
                  Submission failed. Please try again or contact us directly.
                </p>
              )}

              <p className="text-gray-400 text-xs text-center mt-4">
                Response within 48 business hours
              </p>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
